import type { CampaignStatus, UpcomingCamp } from '@dru/types';
import { CAMPAIGN_STATUS_LABEL, EXAM_TYPE_LABEL } from '@dru/types';
import { DataTable, StatusBadge, type Tone } from '@dru/ui';
import { formatDate } from '../../lib/format';
import { formatCount } from './scale';

/**
 * The next examination drives, soonest first: the "upcoming health checkups"
 * table. A company's own on its dashboard; every company's, with the
 * company named, on the platform's.
 *
 * "Employees" is bookings over the plan — 12 / 40 — because the number HR
 * acts on is the gap. A draft has no plan yet and says so.
 */
export function UpcomingCamps({ items, loading, error, onRetry, showCompany, onOpen }: {
  items: UpcomingCamp[] | null | undefined;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  showCompany?: boolean;
  onOpen?: (camp: UpcomingCamp) => void;
}) {
  return (
    <DataTable<UpcomingCamp>
      rows={items ?? []}
      loading={loading}
      error={error}
      onRetry={onRetry}
      rowKey={(c) => c.id}
      onRowClick={onOpen}
      empty={{
        title: 'No camps planned',
        hint: showCompany
          ? 'Campaigns companies plan from today onwards appear here.'
          : 'Plan a campaign to call a cohort for examination on a date.',
      }}
      columns={[
        {
          key: 'camp', header: 'Camp',
          cell: (c) => (
            <div className="min-w-0">
              <div className="truncate font-medium text-neutral-900">{c.name}</div>
              <div className="text-caption text-neutral-500">{EXAM_TYPE_LABEL[c.examinationType] ?? c.examinationType}</div>
            </div>
          ),
        },
        ...(showCompany ? [{
          key: 'company', header: 'Company',
          cell: (c: UpcomingCamp) => <span className="text-neutral-800">{c.company?.name ?? '—'}</span>,
        }] : []),
        {
          key: 'when', header: 'Dates',
          cell: (c) => (
            <span className="whitespace-nowrap tabular-nums text-neutral-800">
              {formatDate(c.startsOn)}{c.endsOn !== c.startsOn && <> – {formatDate(c.endsOn)}</>}
            </span>
          ),
        },
        {
          key: 'employees', header: 'Employees', align: 'right',
          cell: (c) => (
            <span className="tabular-nums text-neutral-800">
              {c.planned === null
                ? <span className="text-neutral-500">{c.raised ? `${formatCount(c.booked)} / ${formatCount(c.raised)}` : 'not counted'}</span>
                : <>{formatCount(c.booked)} <span className="text-neutral-500">/ {formatCount(c.planned)}</span></>}
            </span>
          ),
        },
        {
          key: 'status', header: 'Status', align: 'right',
          cell: (c) => (
            <StatusBadge tone={campTone(c.status)}>{CAMPAIGN_STATUS_LABEL[c.status] ?? c.status}</StatusBadge>
          ),
        },
      ]}
    />
  );
}

/** The same tones the campaign list uses. */
function campTone(s: CampaignStatus): Tone {
  switch (s) {
    case 'COMPLETED': case 'CLOSED': return 'covered';
    case 'ACTIVE': case 'IN_PROGRESS': return 'pending';
    case 'SCHEDULED': return 'dueSoon';
    default: return 'neutral';
  }
}
